/**
 * Builds date-fns patterns from a locale for the UIKIT DayPicker.
 *
 * A trailing literal after the last numeric field (e.g. hu-HU "yyyy. MM. dd.",
 * lv-LV "dd.MM.yyyy.", hr-HR "dd. MM. yyyy.") breaks the UIKIT DayPicker: its IMask
 * MaskedDate validation calls date-fns `format` on an invalid intermediate date and throws
 * "RangeError: Invalid time value", crashing Charging Activity / Invoices when the user opens
 * the custom date range. We therefore strip any trailing non-numeric literal while keeping the
 * locale's native field order. Locale letters inside separators (e.g. the Bulgarian "г") are
 * also dropped because IMask cannot handle them.
 */
const buildLocalePattern = (parts: Intl.DateTimeFormatPart[]): string => {
    const pattern = parts
        .map(part => {
            switch (part.type) {
                case 'day':
                    return 'dd';
                case 'month':
                    return 'MM';
                case 'year':
                    return 'yyyy';
                default:
                    // Keep only safe separator characters; drop letters (e.g. Cyrillic "г").
                    return part.value.replace(/[^\s./-]/g, '');
            }
        })
        .join('');

    // Remove any trailing characters after the last day/month/year token.
    return pattern.replace(/[^dMy]+$/i, '');
};

export const getLocaleDateFormat = (locale: string): string =>
    buildLocalePattern(new Intl.DateTimeFormat(locale).formatToParts(new Date()));

export const getLocaleMonthYearFormat = (locale: string): string =>
    buildLocalePattern(
        new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
        }).formatToParts(new Date())
    );
